import React, { forwardRef } from 'react';

import * as Styled from './resizable-text-area.style';
import { Variants } from './types';

export type ResizableTextAreaProps = {
  value: string;
  onChange: (value: string) => void;
  variant?: Variants;
  onClick?: () => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  isStrikeThrough?: boolean;
  fullWidth?: boolean;
  isFrozen?: boolean;
  // a combobox caller (the guest field) must carry its listbox wiring on the focusable
  // element itself, so these ride through to the textarea rather than a wrapper
  id?: string;
  role?: React.AriaRole;
  autoComplete?: string;
} & Pick<
  React.AriaAttributes,
  | 'aria-activedescendant'
  | 'aria-autocomplete'
  | 'aria-controls'
  | 'aria-expanded'
>;

export const ResizableTextArea = forwardRef<
  HTMLTextAreaElement,
  ResizableTextAreaProps
>(
  (
    {
      value,
      onChange,
      variant = Variants.Body2,
      onClick,
      onFocus,
      onBlur,
      onKeyDown,
      placeholder,
      disabled,
      isStrikeThrough,
      fullWidth,
      isFrozen,
      id,
      role,
      autoComplete,
      ...ariaProps
    },
    ref,
  ) => {
    const _onChange = ({ target }: React.ChangeEvent<HTMLTextAreaElement>) =>
      !isFrozen && onChange(target.value);

    return (
      <Styled.Container $fullWidth={!!fullWidth}>
        <Styled.SizingSpan
          $isStrikeThrough={!!isStrikeThrough}
          $isFaded={!!isFrozen}
          $variant={variant}
        >
          {value || placeholder}
        </Styled.SizingSpan>
        <Styled.NameInput
          {...ariaProps}
          ref={ref}
          id={id}
          role={role}
          autoComplete={autoComplete}
          value={value}
          placeholder={placeholder}
          onChange={_onChange}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          $isStrikeThrough={!!isStrikeThrough}
          $isFaded={!!isFrozen}
          $variant={variant}
          $enabled={!disabled}
        ></Styled.NameInput>
      </Styled.Container>
    );
  },
);
